// whiteboard.* — non-stroke whiteboard operations.
//
// MVP: clear (uniform pub/sub) + imagepaste 3-stage flow.
// Eraser is folded into stroke.* as `pen='eraser'` (protocol-events.md §4.1).
// (protocol-events.md §4)
import { z } from 'zod';
import { MAX_REGION_ITEMS } from '../constants.js';
import { snapshotRefSchema } from './room.js';
import { strokeCancelRejectReasonSchema } from './stroke.js';
// ─── whiteboard.clear ──────────────────────────────────────────────────────
//
// Uniform pub/sub. `layerId` distinguishes two clear modes (W3 Classic
// layers, consult 2026-05-26):
//
//   - `layerId` PRESENT  → clear **only that layer**. The room's session
//     **continues** (no sessionId rotation); the cleared layer is captured by
//     the normal 5-second snapshot trigger (architecture.md §14 「layer 単独
//     clear は session 継続」).
//   - `layerId` ABSENT   → clear **every layer** (full wipe). Drives a
//     sessionId rotation + immediate snapshot (architecture.md §14 「全 layer
//     clear のたび新規 sessionId」, SnapshotDispatcher `'clear'` trigger).
//
// The server now PRESERVES `layerId` on the broadcast (it used to drop it in
// the MVP all-clear-only era — see WhiteboardService for the history). Every
// receiver applies the same mode the originator intended.
// (protocol-events.md §4.2)
const clearLayerIdField = z.number().int().min(0).optional();
/** `whiteboard.clear` — client → server request. */
export const whiteboardClearSchema = z.object({
    t: z.literal('whiteboard.clear'),
    /**
     * Target layer. Omit (or send `undefined`) to clear every layer + rotate
     * the session; send a concrete id (Classic = 0/1/2) to clear that one
     * layer while keeping the session alive.
     */
    layerId: clearLayerIdField,
});
/**
 * `whiteboard.clear` — server → all clients broadcast.
 *
 * Carries the same `layerId` the originator sent (or omits it for a full
 * clear), so receivers + the snapshot replay engine all apply the identical
 * per-layer / all-layer semantics.
 */
export const whiteboardClearBroadcastSchema = whiteboardClearSchema.extend({
    uid: z.string(),
    seq: z.number().int().nonnegative(),
    ts: z.number().int().nonnegative(),
});
// ─── whiteboard.imagepaste-* (3-stage flow) ────────────────────────────────
//
// 1. client → server: imagepaste-request (intent + size)
// 2. server → client: imagepaste-token (presigned PUT) | imagepaste-rejected
// 3. client → S3: PUT (out of band)
// 4. client → server: imagepaste-confirm
// 5. server → all: imagepaste (presigned GET URL)
// (protocol-events.md §4.3)
const imagepasteContentTypeSchema = z.enum(['image/png', 'image/jpeg', 'image/gif']);
/**
 * `whiteboard.imagepaste-request` — c→s.
 *
 * Phase 7 Task 7-7 で `width` / `height` を **必須化** (旧版は optional だった).
 * Server 側で原寸を取得する手段がなく (S3 PUT 前なので HEAD すら打てない)、
 * client 側 (Task 7-8) が File API で本来サイズを読み取って request に乗せる
 * 前提に統一. broadcast の `whiteboard.imagepaste { width, height }` も
 * これをそのまま流すだけになるので、server に物理的サイズ判定の責務が出ない.
 */
export const whiteboardImagepasteRequestSchema = z.object({
    t: z.literal('whiteboard.imagepaste-request'),
    contentType: imagepasteContentTypeSchema,
    /** Bytes; server enforces upper bound (`MAX_IMAGEPASTE_BYTES`, MVP=5MB). */
    contentLength: z.number().int().positive(),
    x: z.number(),
    y: z.number(),
    /** Display width (px). Required — client must read it via File API. */
    width: z.number().positive(),
    /** Display height (px). Required — client must read it via File API. */
    height: z.number().positive(),
    /**
     * Target layer to paste onto. Without this the apply side falls back to
     * layer 0, so a paste made while the user had a non-zero layer selected
     * landed on the wrong layer (imagepaste-layer bug A). Mirrors
     * `stroke.start.layerId` exactly (`z.number().int().min(0).default(0)` =
     * optional, default 0) so every drawing op shares one layer-id convention.
     */
    layerId: z.number().int().min(0).default(0),
});
/** `whiteboard.imagepaste-token` — s→c success response. */
export const whiteboardImagepasteTokenSchema = z.object({
    t: z.literal('whiteboard.imagepaste-token'),
    /** Server-issued upload identifier; the client echoes it on confirm. */
    uploadId: z.uuid(),
    putUrl: z.url(),
    /** Epoch ms after which the presigned PUT URL stops working. */
    expiresAt: z.number().int().nonnegative(),
});
/**
 * `whiteboard.imagepaste-rejected` — s→c failure response.
 *
 * `reason` enum (Phase 7 Task 7-7 で確定):
 *   - `'no-permission'`        : caller lacks `Perm.IMAGEPASTE`
 *   - `'content-too-large'`    : `contentLength > MAX_IMAGEPASTE_BYTES` (5 MB)
 *   - `'invalid-content-type'` : not in `imagepasteContentTypeSchema` enum
 *   - `'rate-limited'`         : per-connection 30 秒に 1 回の制限超過
 *   - `'upload-failed'`        : confirm 時 S3 HEAD で object が見つからない
 *                                (PUT 失敗 / TTL 切れ / 別 connection からの偽装)
 *   - `'invalid-upload'`       : confirm の `uploadId` が pendingUploads に
 *                                存在しない / 別 connection 由来 / TTL 切れ
 */
export const whiteboardImagepasteRejectedSchema = z.object({
    t: z.literal('whiteboard.imagepaste-rejected'),
    reason: z.enum([
        'no-permission',
        'content-too-large',
        'invalid-content-type',
        'rate-limited',
        'upload-failed',
        'invalid-upload',
    ]),
    message: z.string().optional(),
    /**
     * rate-limited のクールダウン残り ms。client がカウントダウン表示に使う。
     * `reason === 'rate-limited'` のときのみ載る (他 reason では省略)。server が
     * `lastRequestAt` を持つ唯一の真実源なので、残り時間は server が算出する。
     */
    retryAfterMs: z.number().int().nonnegative().optional(),
});
/** `whiteboard.imagepaste-confirm` — c→s after successful S3 PUT. */
export const whiteboardImagepasteConfirmSchema = z.object({
    t: z.literal('whiteboard.imagepaste-confirm'),
    uploadId: z.uuid(),
});
/**
 * `whiteboard.imagepaste` — s→c broadcast announcing the placed image.
 *
 * `url` is a presigned GET URL with a long enough lifetime (10–30 min) to
 * cover late-join replay from the session buffer.
 * (protocol-events.md §4.4)
 */
export const whiteboardImagepasteSchema = z.object({
    t: z.literal('whiteboard.imagepaste'),
    uid: z.string(),
    url: z.url(),
    x: z.number(),
    y: z.number(),
    width: z.number().positive(),
    height: z.number().positive(),
    /**
     * Layer the image was pasted onto (relayed from the originator's request,
     * imagepaste-layer bug A). Lets every receiver — and the snapshot replay /
     * layer-impact extractor — apply the paste to the correct layer instead of
     * always layer 0. Mirrors `stroke.start.layerId` exactly.
     */
    layerId: z.number().int().min(0).default(0),
    seq: z.number().int().nonnegative(),
    ts: z.number().int().nonnegative(),
});
// ─── whiteboard.region-* (範囲の移動・コピー 4-stage flow) ───────────────────
//
// 範囲を選んで移動 / コピーする (docs/_wip/region-move-2026-10-15.md)。画像貼り付け
// (`imagepaste-*`) と同じ command/event split だが、別イベントにしている:
// 権限判定なし (消しゴムと同じ)・別の rate limit・複数レイヤー分の画像を 1 命令で
// 送る・元の範囲を消す、という点が違い、imagepaste を広げると入口が歪むため。
//
//   [1] c → s    region-request   { requestId, mode, src, dst, items[] }
//   [2] s → c    region-token     { requestId, items[{ srcLayerId, uploadId, putUrl, expiresAt }] }
//             |  region-rejected  { requestId, reason, retryAfterMs? }
//   [3] client → R2  PUT × N (out of band、画像は純 JS で作った PNG = canvas を通さない)
//   [4] c → s    region-confirm   { requestId }
//   [5] s → all  region           (broadcast, persist) — 全 item が揃ったときだけ。部分成功なし。
//
// 適用順は **src を消す (move のみ) → dst に置く** で固定 (src と dst が重なっても
// 一意)。置く画像は R2 の `{roomId}/uploads/{uploadId}.png` (画像貼り付けと同じ key 規則)。
/** 整数ピクセルの矩形 (キャンバス座標)。 */
const regionRectSchema = z.object({
    x: z.number().int(),
    y: z.number().int(),
    w: z.number().int().positive(),
    h: z.number().int().positive(),
});
/** 置き先の左上 (整数)。幅・高さは src の w,h と同じ (拡大縮小は将来)。 */
const regionPointSchema = z.object({
    x: z.number().int(),
    y: z.number().int(),
});
const regionModeSchema = z.enum(['move', 'copy']);
const regionLayerIdSchema = z.number().int().min(0);
/**
 * `whiteboard.region-request` — c→s.
 *
 * - `requestId`: client 生成の UUID。token / rejected / confirm の相関キー。
 * - `src`: 取った範囲 (キャンバス内に収まっていること = server が検証)。
 * - `dst`: 置き先の左上。`dst` + `src.w,h` の矩形がキャンバスからはみ出してもよい
 *   (はみ出した部分は適用側が切り捨てる) が、1px も重ならない置き先は拒否。
 * - `items`: 1 枚〜(レイヤー数)。複数枚のときは各 item で `srcLayerId === dstLayerId`
 *   (= それぞれのレイヤーの中で動かす)。1 枚のときは別レイヤーへ置いてよい。
 *   `contentType` は `image/png` 固定なので持たない。
 */
export const whiteboardRegionRequestSchema = z.object({
    t: z.literal('whiteboard.region-request'),
    requestId: z.uuid(),
    mode: regionModeSchema,
    src: regionRectSchema,
    dst: regionPointSchema,
    items: z
        .array(z.object({
        srcLayerId: regionLayerIdSchema,
        dstLayerId: regionLayerIdSchema,
        /** PNG のバイト数。1 枚 5MB・合計 15MB は server が検証。 */
        contentLength: z.number().int().positive(),
    }))
        .min(1)
        .max(MAX_REGION_ITEMS),
});
/** `whiteboard.region-token` — s→c. 1 回で item の本数ぶんの PUT URL を返す。 */
export const whiteboardRegionTokenSchema = z.object({
    t: z.literal('whiteboard.region-token'),
    requestId: z.uuid(),
    items: z
        .array(z.object({
        /** request の item と対応づけるキー (request 内で一意)。 */
        srcLayerId: regionLayerIdSchema,
        uploadId: z.uuid(),
        putUrl: z.url(),
        /** Epoch ms after which the presigned PUT URL stops working. */
        expiresAt: z.number().int().nonnegative(),
    }))
        .min(1)
        .max(MAX_REGION_ITEMS),
});
/**
 * `whiteboard.region-rejected` — s→c failure response (request / confirm 共通)。
 *
 *   - `'content-too-large'` : 1 枚 > 5MB、または合計 > 15MB
 *   - `'rate-limited'`      : 1 人 (connection) 1 秒に 1 回の制限超過 (`retryAfterMs` あり)
 *   - `'upload-failed'`     : confirm 時の HEAD で object が無い / storage エラー /
 *                             署名失敗 (1 枚でも駄目なら全体失敗)
 *   - `'invalid-upload'`    : requestId が未発行・期限切れ・別 connection 由来、
 *                             または HEAD のサイズが申告と合わない
 *   - `'invalid-region'`    : 座標が整数でない / キャンバス外 / レイヤー範囲外 /
 *                             複数 item なのに srcLayerId !== dstLayerId / 重複
 *   - `'buffer-full'`       : 部屋の session buffer が reject 上限超え (次の snapshot 待ち)
 */
export const whiteboardRegionRejectedSchema = z.object({
    t: z.literal('whiteboard.region-rejected'),
    requestId: z.uuid(),
    reason: z.enum([
        'content-too-large',
        'rate-limited',
        'upload-failed',
        'invalid-upload',
        'invalid-region',
        'buffer-full',
    ]),
    message: z.string().optional(),
    /** `reason === 'rate-limited'` のときのクールダウン残り ms。 */
    retryAfterMs: z.number().int().nonnegative().optional(),
});
/** `whiteboard.region-confirm` — c→s, 全 item の PUT が成功したあと。 */
export const whiteboardRegionConfirmSchema = z.object({
    t: z.literal('whiteboard.region-confirm'),
    requestId: z.uuid(),
});
/**
 * `whiteboard.region` — s→all broadcast (persist: session buffer / stroke log)。
 *
 * **src と dst の両矩形を持つ** (将来の取り消しの衝突判定が両方を見るため。dst の
 * 矩形は `dst.x, dst.y, src.w, src.h`)。`mode: 'copy'` のとき src は消さない。
 * `items[].width/height` は置く PNG の画素数 (現状は常に `src.w × src.h`)。
 * `url` は presigned GET (30 分)。ログからの再生には再署名が要る
 * (`POST /rooms/:id/imagepaste-urls` が basename `{uploadId}.png` でそのまま使える)。
 */
export const whiteboardRegionSchema = z.object({
    t: z.literal('whiteboard.region'),
    uid: z.string(),
    mode: regionModeSchema,
    src: regionRectSchema,
    dst: regionPointSchema,
    items: z
        .array(z.object({
        srcLayerId: regionLayerIdSchema,
        dstLayerId: regionLayerIdSchema,
        url: z.url(),
        width: z.number().int().positive(),
        height: z.number().int().positive(),
    }))
        .min(1)
        .max(MAX_REGION_ITEMS),
    seq: z.number().int().nonnegative(),
    ts: z.number().int().nonnegative(),
});
// ─── whiteboard.region-cancel — 範囲の移動・コピーの「元に戻す」 ─────────────
//
// 線の conflict-aware Undo (`stroke.cancel`) と同じ tombstone 方式
// (docs/_wip/region-move-2026-10-15.md「未決・要検証」/ undo-conflict-aware-2026-07-12.md)。
//
//   [1] c → s    region-cancel          { regionSeq }
//   [2] s → all  region-cancelled       (broadcast, persist) — 承認。tombstone として session buffer /
//                                       stroke log に積まれる (`stroke.cancelled` と同じ扱い)
//             |  s → c  region-cancel-rejected  { regionSeq, reason, conflicts?, retryAfterMs? }
//
// 拒否理由は `strokeCancelRejectReasonSchema` を共有する (`not-ended` は region では出ない)。
// 受信側は未知の regionSeq の cancelled を無視する (冪等)。
/** `whiteboard.region-cancel` — c→s. 取り消したい自分の `whiteboard.region` の server seq。 */
export const whiteboardRegionCancelSchema = z.object({
    t: z.literal('whiteboard.region-cancel'),
    regionSeq: z.number().int().positive(),
});
/**
 * `whiteboard.region-cancelled` — s→all tombstone (server-stamped、session buffer + stroke log)。
 *
 * `regionSeq` は取り消された `whiteboard.region` の seq。矩形 (src / dst) と画像は
 * 受信側が自分の記録 (その seq の region) から引く。`layerIds` は server が元の region の
 * items から埋める「触った層」(srcLayerId ∪ dstLayerId、昇順・重複なし。copy の src 層も
 * 含む保守側の集合) で、`stroke.cancelled.layerId` と同じ理由で付ける: 記録を引けなくても
 * (snapshot / ログ側の層更新判定 `eventLayerExtractor` を含め) どの層が動いたかが分かる。
 * `uid` は stamped (= 取り消した本人 = 元の region の持ち主)。
 */
export const whiteboardRegionCancelledSchema = z.object({
    t: z.literal('whiteboard.region-cancelled'),
    regionSeq: z.number().int().positive(),
    layerIds: z
        .array(regionLayerIdSchema)
        .min(1)
        .max(MAX_REGION_ITEMS * 2),
    uid: z.string(),
    seq: z.number().int().nonnegative(),
    ts: z.number().int().nonnegative(),
});
/**
 * `whiteboard.region-cancel-rejected` — requester への unicast (stamp なし)。
 * `conflicts` は `reason='conflict'` のとき、交差した **線** の identity (上限 20、
 * imagepaste / 別の region / 層 clear 由来は載らない — `stroke.cancel-rejected` と同じ)。
 * `retryAfterMs` は `reason='rate-limited'` のときだけ (線の取り消しと rate limit を共有)。
 */
export const whiteboardRegionCancelRejectedSchema = z.object({
    t: z.literal('whiteboard.region-cancel-rejected'),
    regionSeq: z.number().int().positive(),
    reason: strokeCancelRejectReasonSchema,
    conflicts: z
        .array(z.object({ sid: z.uuid(), uid: z.string() }))
        .max(20)
        .optional(),
    retryAfterMs: z.number().int().nonnegative().optional(),
});
// ─── whiteboard.background-set ─────────────────────────────────────────────
//
// Uniform pub/sub. The room owner (= holder of `Perm.CLEAR`) changes the
// **canvas background color** — the opaque color composited under every layer
// at display / export time (`shared/snapshot.ts` `SnapshotBackground`, NOT a
// layer pixel). Mirrors `whiteboard.clear` (command + server-stamped
// broadcast in one `t`).
//
// Design: docs/_wip/canvas-background-color-2026-06-20.md §4.
//   - Payload carries `rgb` only; the background alpha is fixed opaque (255)
//     server-side, so an owner can't make the canvas backdrop translucent.
//   - Gate is `hasPerm(Perm.CLEAR)` ALONE — deliberately decoupled from
//     `rooms.clearOpen` (unlike `whiteboard.clear`), so opening clear to
//     everyone does NOT also open background changes. (§4.2)
//   - Modelled as a seq-bearing broadcast event (not just room state) so it
//     round-trips through the session buffer / event log and the future
//     絵ログ再生 (Phase 14+) reproduces background changes on the timeline. (§8.5)
/** `whiteboard.background-set` — client → server request (owner only). */
export const whiteboardBackgroundSetSchema = z.object({
    t: z.literal('whiteboard.background-set'),
    /**
     * New canvas background color as a 24-bit RGB int (`0..0xFFFFFF`). Same
     * range as `SnapshotBackground.rgb`. Alpha is intentionally absent — the
     * server applies opaque (255).
     */
    rgb: z.number().int().min(0).max(0xffffff),
});
/**
 * `whiteboard.background-set` — server → all clients broadcast.
 *
 * Server-stamped (`uid`/`seq`/`ts`) so receivers apply the change via
 * `engine.setBackground(rgb, 255)` and the snapshot replay engine reproduces
 * it in order.
 */
export const whiteboardBackgroundSetBroadcastSchema = whiteboardBackgroundSetSchema.extend({
    uid: z.string(),
    seq: z.number().int().nonnegative(),
    ts: z.number().int().nonnegative(),
});
// ─── whiteboard.restore-* (過去キャンバス復元 3-stage flow) ─────────────────
//
// Slice 3 (past-canvas-new-document-2026-07-05.md §4.1/§4.2/§4.5)。「全消し」で
// rotate された過去キャンバス (`room_past_canvases` 索引) を復元し、全員の
// キャンバスを bit-perfect に差し替える (= 新セッションを過去 snapshot の
// レイヤ一式で種付けする「続きを描く」)。
//
//   [1] c → s    whiteboard.restore-request   pastCanvasId を指定して復元要求
//   [2] s → c    whiteboard.restore-rejected  失敗応答
//   [3] s → all  whiteboard.restore           成功 broadcast (server-stamped, loggable)
//
// imagepaste と同じ command/event split を採る (サーバ側の R2 種コピーという
// 非同期・失敗しうる処理を挟むため。clear の uniform pub/sub 型は不採用)。
//
// 権限: `Perm.CLEAR` 保有者のみ。`rooms.clearOpen` バイパスは**適用しない**
// (§4.2 — clearOpen は「誰でもまっさらから描き始めてよい」という前向きの開放
// であり、部屋の履歴を遡って全員のキャンバスを書き換える復元には及ばない)。
/**
 * `whiteboard.restore-request` — c→s.
 *
 * `pastCanvasId` は `room_past_canvases.id` (UUIDv7)。過去キャンバス一覧
 * endpoint (`GET /rooms/:roomId/past-canvases`) が返す `id` をそのまま送る。
 */
export const whiteboardRestoreRequestSchema = z.object({
    t: z.literal('whiteboard.restore-request'),
    pastCanvasId: z.uuid(),
});
/**
 * `whiteboard.restore-rejected` — s→c (response 失敗)。
 *
 * reason:
 *   - `'no-permission'`    : `Perm.CLEAR` 非保有 (clearOpen バイパス不可)
 *   - `'not-found'`        : pastCanvasId が当該 room の索引に無い
 *   - `'snapshot-missing'` : 索引行に snapshotMeta が無い / R2 から種 snapshot
 *                            が消えている (purge 済・backfill 前 orphan 等)
 *   - `'busy'`             : 同一 room で別の復元が進行中 (多重復元ガード)
 *   - `'restore-failed'`   : R2 種コピー等サーバ内部の失敗
 */
export const whiteboardRestoreRejectedSchema = z.object({
    t: z.literal('whiteboard.restore-rejected'),
    reason: z.enum(['no-permission', 'not-found', 'snapshot-missing', 'busy', 'restore-failed']),
});
/**
 * `whiteboard.restore` — server → all clients broadcast。
 *
 * **loggable event** (stroke log に焼き込まれる、§4.5-1)。ログ再生 (タイムラプス
 * T1) はこのイベントを「系譜マーカー」として解釈し、`restoredFromSessionId` /
 * `restoredFromSeq` の耐久 ID から復元元ドキュメントの seq 範囲を無音再適用して
 * 土台を作る (§4.5-2)。**`snapshot` の presigned URL は log 再生では使わない**
 * (welcome 同様 TTL 60s で期限切れするため。`whiteboard.imagepaste.url` の
 * 期限切れ caveat と同じ扱いだが、restore は耐久 ID で完全に再構築できる点で
 * imagepaste より強い)。
 *
 * ライブ受信側 (接続中クライアント) は `snapshot` (welcome の `snapshotRef` と
 * 同形、新セッションへコピー済みの種 snapshot への presigned URL 一式) を
 * `SnapshotLoader` で読み込み、全レイヤを差し替える。
 */
export const whiteboardRestoreSchema = z.object({
    t: z.literal('whiteboard.restore'),
    uid: z.string(),
    /** 復元元の `room_past_canvases.id`。UI/系譜デバッグ用 (行は room 削除で cascade するため replay はこれに依存しない)。 */
    pastCanvasId: z.uuid(),
    /** 復元元の旧 sessionId (R2 path `{roomId}/sessions/{sessionId}/`)。系譜の耐久 ID。 */
    restoredFromSessionId: z.string().min(1),
    /** 復元種 snapshot に焼き込まれた最大 broadcast seq (= 系譜タイムラプスの連結上端、§4.5-3)。 */
    restoredFromSeq: z.number().int().nonnegative(),
    /** 新セッションへコピー済みの種 snapshot への presigned 参照 (ライブ再ロード専用)。 */
    snapshot: snapshotRefSchema,
    seq: z.number().int().nonnegative(),
    ts: z.number().int().nonnegative(),
    /**
     * 荒らし復旧 UI 第 1 段階の追補 (vandalism-recovery-ui-2026-09-27.md §2
     * 「要検証: 復元直後に自動保存された行の id がクライアントに届くか」への対応、
     * 作者合意 A 案): この復元が rotate する**直前の現行セッション**を自動保存した
     * `room_past_canvases.id`。この id で再度 `whiteboard.restore-request` を送れば
     * 「元に戻す」(= 今回の復元前の状態に戻す) ができる。
     *
     * - `undefined` = 自動保存の対象になるセッションが無かった (rotate 直前の
     *   現行セッションが一度も snapshot を publish していないまっさらな状態 —
     *   `WhiteboardRestoreService` の記録条件と同じ。この場合「元に戻す」は
     *   そもそも意味を持たない)。
     * - **id が届いても、行の記録自体は fire-and-forget** (`recordPastCanvas`)
     *   なので失敗しうる。この id を `whiteboard.restore-request` の
     *   `pastCanvasId` に指定しても行が実在しなければ通常どおり
     *   `whiteboard.restore-rejected { reason: 'not-found' }` になる —
     *   client はこれを「元に戻せませんでした」として扱う想定 (行の存在を
     *   事前に確認する専用 API は設けない)。
     */
    autoSavedPastCanvasId: z.uuid().optional(),
});
//# sourceMappingURL=whiteboard.js.map